import { ElementType, useCallback, useEffect, useState } from "react";

import { useUpdateCosignPropertyManagerUuid } from "manager/hooks/api/integrationQueries";
import ConfirmationModal from "shared/components/Modals/ConfirmationModal";
import { NOTIFICATIONS } from "shared/config/constants";
import useModal from "shared/hooks/useModal";
import { openNotification } from "shared/utils/ui";

import {
  COSIGN_PM_UUID_CONFIRMATION_COPY,
  getCosignPmUuidDraftError,
} from "./cosignDashboard.config";

interface OpenCosignPmUuidConfirmationParams {
  draftUuid: string;
  propertyManagerUuid?: string | null;
  openModalDialog: (
    modal: ElementType,
    context: Record<string, unknown>
  ) => void;
  onConfirm: (nextUuid: string) => Promise<void>;
}

export const openCosignPmUuidConfirmation = ({
  draftUuid,
  propertyManagerUuid,
  openModalDialog,
  onConfirm,
}: OpenCosignPmUuidConfirmationParams): string | null => {
  const validationError = getCosignPmUuidDraftError(
    draftUuid,
    propertyManagerUuid
  );
  if (validationError) {
    return validationError;
  }

  const trimmed = draftUuid.trim();
  openModalDialog(ConfirmationModal, {
    title: "Change Cosign account ID",
    submitButtonLabel: "Confirm",
    subtitle: COSIGN_PM_UUID_CONFIRMATION_COPY,
    onConfirm: () => onConfirm(trimmed),
  });

  return null;
};

export const extractPmUuidErrorMessage = (error: any, fallback: string) =>
  error?.errors?.error ||
  error?.errors?.nonFieldErrors?.[0] ||
  error?.response?.data?.error ||
  fallback;

export const getPmUuidErrorStatusCode = (error: any): number | undefined =>
  error?.statusCode ?? error?.response?.status ?? error?.status;

export const isCosignPmUuidUnreachableError = (error: any): boolean =>
  getPmUuidErrorStatusCode(error) === 502;

interface UseCosignPmUuidEditOptions {
  propertyManagerUuid?: string | null;
}

export const useCosignPmUuidEdit = ({
  propertyManagerUuid,
}: UseCosignPmUuidEditOptions) => {
  const { openModalDialog } = useModal();
  const {
    updateCosignPropertyManagerUuid,
    isUpdatingCosignPropertyManagerUuid,
  } = useUpdateCosignPropertyManagerUuid();

  const [isEditing, setIsEditing] = useState(false);
  const [draftUuid, setDraftUuid] = useState(propertyManagerUuid || "");
  const [inlineError, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraftUuid(propertyManagerUuid || "");
      setInlineError(null);
    }
  }, [propertyManagerUuid, isEditing]);

  const startEditing = useCallback(() => {
    setDraftUuid(propertyManagerUuid || "");
    setInlineError(null);
    setIsEditing(true);
  }, [propertyManagerUuid]);

  const cancelEditing = useCallback(() => {
    setDraftUuid(propertyManagerUuid || "");
    setInlineError(null);
    setIsEditing(false);
  }, [propertyManagerUuid]);

  const submitUpdate = useCallback(
    (nextUuid: string) =>
      new Promise<void>((resolve) => {
        updateCosignPropertyManagerUuid(nextUuid, {
          onSuccess: () => {
            setIsEditing(false);
            setInlineError(null);
            openNotification(
              "Cosign account ID updated successfully.",
              NOTIFICATIONS.info
            );
            resolve();
          },
          onError: (error: any) => {
            const message = extractPmUuidErrorMessage(
              error,
              "Unable to update the Cosign account ID. Please try again."
            );

            if (isCosignPmUuidUnreachableError(error)) {
              openNotification(
                "Unable to reach Cosign. The account ID was not updated.",
                NOTIFICATIONS.error
              );
            } else {
              setInlineError(message);
            }

            resolve();
          },
        });
      }),
    [updateCosignPropertyManagerUuid]
  );

  const saveDraft = useCallback(() => {
    const validationError = openCosignPmUuidConfirmation({
      draftUuid,
      propertyManagerUuid,
      openModalDialog,
      onConfirm: submitUpdate,
    });

    if (validationError) {
      setInlineError(validationError);
      return;
    }

    setInlineError(null);
  }, [draftUuid, openModalDialog, propertyManagerUuid, submitUpdate]);

  return {
    isEditing,
    draftUuid,
    inlineError,
    isSaving: isUpdatingCosignPropertyManagerUuid,
    setDraftUuid,
    startEditing,
    cancelEditing,
    saveDraft,
  };
};
